import { useTimeout, useTranslate } from "ra-core";
import type { ReactNode } from "react";

import { Skeleton } from "@/components/ui/skeleton";
import { ListPlaceholder } from "./ListPlaceholder.tsx";

/**
 * Skeleton loading state for a mobile SimpleList (row/card placeholders,
 * never a spinner). Announced to assistive tech via `aria-busy` +
 * `role="status"` and a visually hidden "Cargando…" label.
 */
export const SimpleListLoading = (props: SimpleListLoadingProps) => {
  const {
    className,
    hasLeftAvatarOrIcon,
    hasRightAvatarOrIcon,
    hasSecondaryText,
    hasTertiaryText,
    nbFakeLines = 5,
    ...rest
  } = props;

  const translate = useTranslate();
  const oneSecondHasPassed = useTimeout(1000);

  return oneSecondHasPassed ? (
    <ul aria-busy="true" role="status" className={className} {...rest}>
      <span className="sr-only">{translate("crm.common.loading")}</span>
      {times(nbFakeLines, (key) => (
        <li
          key={key}
          aria-hidden="true"
          className="flex items-center space-x-3 p-3"
        >
          {hasLeftAvatarOrIcon && (
            <Skeleton className="w-10 h-10 rounded-full flex-shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <div className="mb-1">
              <ListPlaceholder className="w-1/3 inline-block mb-1" />
              {hasTertiaryText && (
                <span className="float-right opacity-55 min-w-[10vw]">
                  <ListPlaceholder />
                </span>
              )}
            </div>
            {hasSecondaryText && <ListPlaceholder className="w-1/4" />}
          </div>
          {hasRightAvatarOrIcon && (
            <Skeleton className="w-10 h-10 rounded-full flex-shrink-0" />
          )}
        </li>
      ))}
    </ul>
  ) : null;
};

const times = (nbChildren: number, fn: (key: number) => ReactNode) =>
  Array.from({ length: nbChildren }, (_, key) => fn(key));

export interface SimpleListLoadingProps {
  className?: string;
  hasLeftAvatarOrIcon?: boolean;
  hasRightAvatarOrIcon?: boolean;
  hasSecondaryText?: boolean;
  hasTertiaryText?: boolean;
  nbFakeLines?: number;
}
